import React from 'react';
import PrismaticBurst from './PrismaticBurst';

interface LogoWithBurstProps {
  src: string;
  alt: string;
  size: number;
  className?: string;
  imgClassName?: string;
  eager?: boolean;
  /** Show the burst always (e.g. on touch devices). Otherwise on group hover. */
  alwaysOn?: boolean;
  showBurst?: boolean;
}

/**
 * Renders the Ink & Insanity book logo with a Prismatic Burst effect
 * masked to each "leaf eye" opening. The burst is hidden by default
 * and fades in when the parent .group is hovered.
 */
const LogoWithBurst: React.FC<LogoWithBurstProps> = ({
  src,
  alt,
  size,
  className = '',
  imgClassName = '',
  eager = true,
  alwaysOn = false,
  showBurst = true,
}) => {
  // Eye geometry (percentages relative to logo box).
  // Tuned to match the two leaf-shaped openings in the book logo.
  const eyes = [
    { left: '32%', top: '40%', rotate: 18 },
    { left: '68%', top: '40%', rotate: -18 },
  ];

  const eyeWidth = size * 0.30;
  const eyeHeight = size * 0.22;

  const visibilityClass = alwaysOn
    ? 'opacity-100'
    : 'opacity-0 group-hover:opacity-100';

  return (
    <span
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size }}
      aria-hidden={false}
    >
      {/* Burst layers — one per eye, masked to a leaf-shaped ellipse */}
      {showBurst && eyes.map((eye, i) => (
        <span
          key={i}
          className={`pointer-events-none absolute transition-opacity duration-500 ${visibilityClass}`}
          style={{
            left: eye.left,
            top: eye.top,
            width: eyeWidth,
            height: eyeHeight,
            transform: `translate(-50%, -50%) rotate(${eye.rotate}deg)`,
            clipPath: 'ellipse(50% 50% at 50% 50%)',
            WebkitClipPath: 'ellipse(50% 50% at 50% 50%)',
            zIndex: 1,
          }}
        >
          <PrismaticBurst
            intensity={2.4}
            speed={0.7}
            animationType="rotate3d"
            distort={2.5}
            colors={['#73BA9B', '#D9DBBC', '#7B5CFF', '#FF6BD6', '#FFB347']}
            mixBlendMode="normal"
          />
        </span>
      ))}

      {/* Logo on top */}
      <img
        src={src}
        alt={alt}
        width={size}
        height={size}
        loading={eager ? 'eager' : 'lazy'}
        decoding={eager ? 'sync' : 'async'}
        {...({ fetchpriority: eager ? 'high' : 'auto' } as any)}
        className={`relative z-[2] w-full h-full object-contain ${imgClassName}`}
      />
    </span>
  );
};

export default LogoWithBurst;